import { useState, useEffect, useCallback } from 'react'
import { CHECKOUT_STORAGE_KEY } from '../../../constants/storageKeys'
import { Address } from '../../../shared/types/battery'
import { PaymentMethod } from '../../../shared/types/battery'
import { AppliedCoupon } from '../../../shared/utils/couponHelpers'

interface CheckoutStorageData {
  shippingAddress: Address
  billingAddress: Address
  customerInfo: {
    email: string
    name: string
    document: string
  }
  selectedPaymentMethod: PaymentMethod | null
  selectedShipping: string
  currentStep: number
  appliedCoupons: AppliedCoupon[]
  deliveryMethod?: 'shipping' | 'pickup' | null
  willReturnScrap?: boolean
  orderId?: string | null
  clientSecret?: string | null
  cartFingerprint?: string | null
}

interface UseCheckoutStorageReturn {
  isDataLoaded: boolean
  savedData: Partial<CheckoutStorageData> | null
  saveData: (data: Partial<CheckoutStorageData>) => void
  clearData: () => void
}

export const useCheckoutStorage = (): UseCheckoutStorageReturn => {
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [savedData, setSavedData] = useState<Partial<CheckoutStorageData> | null>(null)

  // Carregar dados do localStorage ao montar
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CHECKOUT_STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)

        // Verificar se os dados não estão expirados (24 horas)
        const savedAt = parsed.savedAt ? new Date(parsed.savedAt) : null
        const now = new Date()
        const hoursDiff = savedAt ? (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60) : 999

        if (hoursDiff > 24) {
          // Dados muito antigos, limpar
          localStorage.removeItem(CHECKOUT_STORAGE_KEY)
          console.log('Dados do checkout expiraram e foram removidos')
          setSavedData(null)
        } else {
          setSavedData(parsed)
          console.log('Dados do checkout restaurados com sucesso')
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados do localStorage:', error)
      // Se houver erro ao parsear, limpar dados corrompidos
      localStorage.removeItem(CHECKOUT_STORAGE_KEY)
      setSavedData(null)
    } finally {
      setIsDataLoaded(true)
    }
  }, [])

  const saveData = useCallback((data: Partial<CheckoutStorageData>) => {
    if (!isDataLoaded) return

    const dataToSave = {
      ...data,
      savedAt: new Date().toISOString()
    }

    try {
      localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(dataToSave))
      setSavedData(dataToSave)
    } catch (error) {
      console.error('Erro ao salvar dados no localStorage:', error)
    }
  }, [isDataLoaded])

  const clearData = useCallback(() => {
    localStorage.removeItem(CHECKOUT_STORAGE_KEY)
    setSavedData(null)
  }, [])

  return {
    isDataLoaded,
    savedData,
    saveData,
    clearData
  }
}

